// open-side-info.ts — generalized "what is the side-info drawer currently showing" registry, so an
// invalidation / websocket update that actually TOUCHES the open object re-renders it IN PLACE
// instead of the old blanket "re-fetch the whole card on any change" sledgehammer.
//
// WHY: the side-info drawer (#div_refresh_side_info) is polymorphic — a general item, an inventory
// container you can drag in/out of, a double-clicked encounter creature, a PC, or a status effect.
// All of those can desync when something changes them elsewhere (another client, combat, a move).
// The fresh data already arrives over the existing caster: channels (caster_update /
// caster_update_delta). This module is the missing client glue: remember the open object's id, and
// when a change's affected-id set includes it, re-render it INSTANTLY from the client caches
// (window.ftRenderItemFromCache → casterCache/objectCache, ZERO network).
//
// CACHE-ONLY BY DESIGN: this surgical re-render NEVER falls back to a server fetch. Item/inventory
// cards repaint via window.ftRenderItemFromCache; a STATUS-INSTANCE card (double-clicked persistent-AOE
// zone / console chip, rendered client-side by the status-effect bundle) repaints via
// window.ftRenderStatusInstanceFromCache — its status_id is unioned into the affected set below
// (collectStatusIds) so a caster_update touching the status's owner reaches it. Creature / PC cards
// keep their existing live mechanisms — the info-panel sub-widgets (active-panel.ts) already repaint a
// creature's essence/health/AC from the same cache — and the mover's own openItemInfo (template.js)
// stays the authoritative refresh after a deliberate action. Routing routine websocket updates through
// a server fetch would turn every HP delta during combat into a card re-fetch (a fetch storm + the
// very sledgehammer we're removing).
// On a cache miss we simply skip: the drawer keeps its last paint until the next interaction —
// no worse than today (where the drawer didn't react to websocket updates at all).
//
// No new websocket message type and no new cache (no `inventoryCache`): inventory already lives
// inside the caster/object blobs and is resolved by id via find-cached-item.ts.

import { collectInventoryIds, collectStatusIds } from './find-cached-item';

const SIDE_INFO_TARGET_ID = 'div_refresh_side_info';

interface OpenSideInfo {
    id: string;        // uppercased identity of the open object (item_id / inventory_id / ec_id / status_id)
    targetId: string;  // the container the card is painted into
}

type OpenSideInfoWindow = Window & {
    isSideInfoPanelOpen?: () => boolean;
    // Set by a canvas minigame that has claimed #div_refresh_side_info (lockpick.ts).
    __ftSideInfoMinigameActive?: boolean;
    ftRenderItemFromCache?: (id: string, container: HTMLElement) => boolean;
    ftRenderStatusInstanceFromCache?: (id: string, container: HTMLElement) => unknown;
};

let open: OpenSideInfo | null = null;

/**
 * Remember what the side-info drawer is currently showing. Called by every open path (the cache
 * render in render-from-cache.ts and the server path in template.js:openItemInfo). A falsy id
 * clears the registry.
 */
function setOpenSideInfo(id: string, targetId: string = SIDE_INFO_TARGET_ID): void {
    if (!id) { open = null; return; }
    open = { id: String(id).toUpperCase(), targetId: targetId || SIDE_INFO_TARGET_ID };
}

function clearOpenSideInfo(): void {
    open = null;
}

/** The uppercased id the side-info drawer is currently registered as showing (or null). Lets a
 * deliberate-edit repaint (inventory-edit-form bundle) confirm the open card is THIS item and
 * re-render it directly, bypassing the isSideInfoPanelOpen layout gate. */
function getOpenSideInfoId(): string | null {
    return open ? open.id : null;
}

/**
 * Re-render the open side-info card iff a change touched it. `affected` is the set of ids the change
 * carried — for a caster_update that's the caster id plus the inventory ids it holds. Omitting
 * `affected` re-renders unconditionally.
 *
 * Cache-only (see file header): paints from window.FateTide.casterCache/objectCache via
 * ftRenderItemFromCache; if that can't satisfy the render (server-rendered kind, cache miss,
 * server-only control gate) it does nothing. No-op while the drawer is closed — re-opening renders
 * cache-first from the now-fresh cache anyway.
 */
function rerenderOpenSideInfo(affected?: Iterable<string> | string | null): void {
    try {
        (window as Window & { ftRefreshWorldContainers?: () => void }).ftRefreshWorldContainers?.();
        if (!open) return;

        const w = window as OpenSideInfoWindow;

        const target = document.getElementById(open.targetId);
        if (!target) return;

        // FT3D moves the canonical #div_refresh_side_info host out of the retired drawer and into
        // the Workspace window. isSideInfoPanelOpen() intentionally reports the old drawer as
        // closed on that route, so using it as the sole visibility gate suppresses every live item
        // repaint even while the Workspace card is on screen. Treat that mounted FT3D host as open;
        // ordinary pages retain the legacy drawer gate below.
        const targetClosest = typeof target.closest === 'function'
            ? target.closest('[data-ft3d-side-info-mount]')
            : null;
        const isFt3dWorkspaceHost = open.targetId === SIDE_INFO_TARGET_ID
            && document.documentElement?.classList?.contains('ft3d-hud-active') === true
            && targetClosest !== null;

        // Closed drawer → nothing on screen; skip (reopen reads the fresh cache).
        if (
            !isFt3dWorkspaceHost
            && typeof w.isSideInfoPanelOpen === 'function'
            && !w.isSideInfoPanelOpen()
        ) return;

        // A live canvas MINIGAME owns the drawer (lockpick — claimSideInfoForMinigame). Repainting
        // here would replace its canvas mid-attempt, and the minigame reads a detached canvas as
        // "panel replaced under us" → it CANCELS the attempt server-side. That was the 2026-07-24
        // "lockpicking cancels itself after a second or two with no user input" bug: the attempt's
        // own status/AP writes broadcast the caster_update that landed right here. The minigame
        // refreshes the drawer itself when it ends.
        if (w.__ftSideInfoMinigameActive) return;

        // Scope: only re-render when this change actually touched the open object.
        if (affected !== undefined && affected !== null) {
            const list = typeof affected === 'string' ? [affected] : Array.from(affected);
            const set = new Set(list.map((x) => String(x).toUpperCase()));
            if (!set.has(open.id)) return;
        }

        // Instant cache render for object-card-eligible items/inventory. Returns false for a
        // creature/PC/status-instance, a cache miss, a shell-only entry, or a server-only control gate
        // WITHOUT touching the container — so on false we can safely try the status renderer next.
        let painted = false;
        if (typeof w.ftRenderItemFromCache === 'function') {
            painted = w.ftRenderItemFromCache(open.id, target);
        }
        // Status-instance card: ftRenderItemFromCache rejects status instances, so a double-clicked
        // persistent-AOE zone / console chip repaints here from the warm cache (no server fetch). A
        // creature/PC stays on its own live mechanism (this is a no-op miss for those).
        if (!painted && typeof w.ftRenderStatusInstanceFromCache === 'function') {
            w.ftRenderStatusInstanceFromCache(open.id, target);
        }
    } catch (e) {
        console.warn('rerenderOpenSideInfo failed', e);
    }
}

Object.assign(window, {
    ftSetOpenSideInfo: setOpenSideInfo,
    ftClearOpenSideInfo: clearOpenSideInfo,
    ftGetOpenSideInfoId: getOpenSideInfoId,
    ftRerenderOpenSideInfo: rerenderOpenSideInfo,
    // Exposed for the websocket bundle (different bundle) to build a caster_update's affected-id set:
    // the caster id ∪ every inventory id ∪ every status id the caster carries. The status ids let an
    // OPEN status-instance card (registered under its status_id) repaint when its owner updates — kept
    // on this name so the WS call sites need no change. Optional-chained there so a page without this
    // bundle just falls back to matching the caster id alone.
    ftCollectInventoryIds: (blob: unknown) => [...collectInventoryIds(blob), ...collectStatusIds(blob)],
});

export { setOpenSideInfo, clearOpenSideInfo, getOpenSideInfoId, rerenderOpenSideInfo };
